// src/pages/EditArtifact.jsx
import React, { useEffect, useState } from "react";
import api from "../api";
import { Box, Paper, TextField, Button, Typography, Alert } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

export default function EditArtifact() {
  const { id } = useParams();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [transcript, setTranscript] = useState("");
  const [image, setImage] = useState(null);
  const [audio, setAudio] = useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const navigate = useNavigate();

  // Load existing artifact
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await api.get(`/api/artifacts/${id}`);   // 🔥 relative
        setTitle(res.data.title);
        setDescription(res.data.description);
        setTranscript(res.data.transcript || "");
      } catch (err) {
        console.error(err);
        setError(
          err?.response?.status === 404
            ? "Artifact not found."
            : "Failed to load artifact. Please check WiFi / Server."
        );
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      setSaving(true);

      const fd = new FormData();
      fd.append("title", title);
      fd.append("description", description);
      fd.append("transcript", transcript);

      if (image) fd.append("image", image);
      if (audio) fd.append("audio", audio);

      await api.put(`/api/artifacts/${id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSuccess("Artifact updated successfully.");
      setTimeout(() => navigate("/admin/artifacts"), 1200);
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.message ||
          "Update failed. Please check server / Wi-Fi connection."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <Typography sx={{ textAlign: "center", mt: 4 }}>
        Loading artifact…
      </Typography>
    );

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Edit Artifact
      </Typography>

      <Paper sx={{ p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {!error && (
          <form onSubmit={submit}>
            <TextField
              label="Title"
              fullWidth
              required
              sx={{ mb: 2 }}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />

            <TextField
              label="Description"
              fullWidth
              multiline
              rows={4}
              required
              sx={{ mb: 2 }}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />

            {/* Transcript Editor */}
            <TextField
              label="Transcript"
              fullWidth
              multiline
              rows={6}
              sx={{ mb: 2 }}
              value={transcript}
              onChange={(e) => setTranscript(e.target.value)}
              placeholder="Edit narration transcript…"
            />

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2">
                Replace Image (optional)
              </Typography>
              <input
                accept="image/*"
                type="file"
                onChange={(e) => setImage(e.target.files[0])}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2">
                Replace Audio (optional)
              </Typography>
              <input
                accept="audio/*"
                type="file"
                onChange={(e) => setAudio(e.target.files[0])}
              />
            </Box>

            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </form>
        )}
      </Paper>
    </Box>
  );
}
